import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtEUR, fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Plus, Search, Check, ChevronRight, Trash2, CheckCircle2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useSortable } from "@/lib/sortable";
import { slaOverzichtNavOp } from "@/lib/overzichtNav";
import { usePersistedState } from "@/lib/persist";
import { LaadBalk, TabelLaadRij } from "@/components/LaadIndicator";
import { toast } from "sonner";

const FASES = ["Cold", "Terugbellen", "Sample opsturen", "Sample opgestuurd", "Onderhandeling", "Deal!", "Afgevallen", "Webshoporder", "Betaald, nog niet verstuurd"];

function OrgPicker({ value, onChange }) {
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/organisaties").then(r => setOrgs(r.data)); }, []);
  const filtered = useMemo(() => orgs.filter(o => o.naam.toLowerCase().includes(q.toLowerCase())), [orgs, q]);
  const sel = orgs.find(o => o.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start" data-testid="vk-org-picker">{sel ? sel.naam : "Selecteer organisatie…"}</Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0">
        <div className="p-2 border-b border-border"><Input autoFocus placeholder="Zoek…" value={q} onChange={(e)=>setQ(e.target.value)} className="h-9" /></div>
        <div className="max-h-64 overflow-y-auto">
          {filtered.map(o => (
            <button key={o.id} onClick={()=>{onChange(o.id); setOpen(false);}} className="w-full text-left px-3 py-2 hover:bg-secondary text-sm flex justify-between">
              <span>{o.naam}</span>{o.id === value && <Check className="h-4 w-4 text-primary" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NewKansDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const empty = { relatie_id: "", naam: "", fase: "Cold", bedrag: 0, datum: "", regels: [] };
  const [f, setF] = useState(empty);
  const [producten, setProducten] = useState([]);
  const [klantPrijzen, setKlantPrijzen] = useState({}); // product_id -> {verkoopprijs, inkoopprijs}
  useEffect(() => { if (open) { setF(empty); setKlantPrijzen({}); api.get("/producten").then(r => setProducten(r.data)); } /* eslint-disable-next-line */ }, [open]);

  // Bij organisatie-wissel: laad klantspecifieke prijzen
  useEffect(() => {
    if (!f.relatie_id) { setKlantPrijzen({}); return; }
    api.get(`/organisaties/${f.relatie_id}/prijzen`).then(r => {
      setKlantPrijzen(Object.fromEntries(r.data.map(x => [x.product_id, x])));
    }).catch(()=>{});
  }, [f.relatie_id]);

  // Effectieve prijsmap: standaard product + override klantprijs
  const prodMap = useMemo(() => {
    const m = {};
    producten.forEach(p => {
      const cust = klantPrijzen[p.id];
      m[p.id] = {
        ...p,
        verkoopprijs: cust && cust.verkoopprijs != null ? Number(cust.verkoopprijs) : Number(p.verkoopprijs || 0),
        inkoopprijs: cust && cust.inkoopprijs != null ? Number(cust.inkoopprijs) : Number(p.inkoopprijs || 0),
        is_klantprijs: !!cust,
        standaard_verkoopprijs: Number(p.verkoopprijs || 0),
      };
    });
    return m;
  }, [producten, klantPrijzen]);

  const addProduct = (pid) => {
    if (!pid || f.regels.find(r => r.product_id === pid)) return;
    setF({ ...f, regels: [...f.regels, { product_id: pid, aantal: 1 }] });
  };
  const removeProduct = (pid) => setF({ ...f, regels: f.regels.filter(r => r.product_id !== pid) });
  const setAantal = (pid, n) => setF({ ...f, regels: f.regels.map(r => r.product_id === pid ? { ...r, aantal: n } : r) });

  const totaalRegels = useMemo(() => f.regels.reduce((s, r) => {
    const p = prodMap[r.product_id]; return s + (Number(r.aantal)||0) * (Number(p?.verkoopprijs)||0);
  }, 0), [f.regels, prodMap]);

  const submit = async () => {
    if (!f.relatie_id) { toast.error("Selecteer organisatie"); return; }
    try {
      const bedragFinal = totaalRegels > 0 ? totaalRegels : (parseFloat(String(f.bedrag).replace(",", ".")) || 0);
      const payload = { relatie_id: f.relatie_id, naam: f.naam, fase: f.fase, bedrag: bedragFinal,
                        datum: f.datum ? new Date(f.datum).toISOString() : null };
      const { data } = await api.post("/verkoopkansen", payload);
      // attach regels — backend past zelf klantprijs toe via code-lookup, maar we sturen ook expliciet
      for (const reg of f.regels) {
        const p = prodMap[reg.product_id]; if (!p) continue;
        await api.post("/verkoopkansregels", {
          verkoopkans_id: data.id,
          code: p.sku, product: p.naam, productgroep: p.productgroep || "",
          aantal: Number(reg.aantal) || 1,
          inkoopprijs: p.inkoopprijs || 0, verkoopprijs: p.verkoopprijs || 0,
        });
      }
      toast.success(`Aangemaakt${f.regels.length ? ` met ${f.regels.length} artikel(en)` : ""}`);
      setOpen(false); onCreated(data);
    } catch { toast.error("Opslaan mislukt"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-kans-btn"><Plus className="h-4 w-4 mr-2" />Nieuwe kans</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Nieuwe verkoopkans</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div><Label>Organisatie</Label><OrgPicker value={f.relatie_id} onChange={(v)=>setF({...f,relatie_id:v})} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Naam</Label><Input data-testid="nk-naam" value={f.naam} onChange={(e)=>setF({...f,naam:e.target.value})} placeholder="Bijv. Q3 aanbod" /></div>
            <div><Label>Fase</Label>
              <Select value={f.fase} onValueChange={(v)=>setF({...f,fase:v})}>
                <SelectTrigger data-testid="nk-fase"><SelectValue /></SelectTrigger>
                <SelectContent>{FASES.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="pt-2 border-t border-border">
            <div className="flex items-center justify-between mb-1">
              <Label>Artikelen (optioneel)</Label>
              <span className="text-sm text-muted-foreground">Totaal: <strong className="text-foreground">{fmtEUR(totaalRegels)}</strong></span>
            </div>
            <Select value="" onValueChange={addProduct}>
              <SelectTrigger data-testid="nk-prod-picker"><SelectValue placeholder="+ Voeg artikel toe…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {producten.filter(p => !f.regels.find(r => r.product_id === p.id)).map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.sku} — {p.naam} ({fmtEUR(p.verkoopprijs)})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {f.regels.length > 0 && (
              <div className="mt-2 border border-border rounded-md divide-y divide-border">
                {f.regels.map(r => {
                  const p = prodMap[r.product_id]; if (!p) return null;
                  return (
                    <div key={r.product_id} className={`p-2 flex items-center gap-2 ${p.is_klantprijs ? "bg-primary/[0.04]" : ""}`} data-testid={`nk-row-${r.product_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate flex items-center gap-1.5">
                          {p.naam}
                          {p.is_klantprijs && <span className="text-[10px] uppercase font-bold bg-primary/15 text-primary rounded px-1.5 py-0.5" data-testid={`klantprijs-badge-${r.product_id}`}>klantprijs</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {p.sku} · {fmtEUR(p.verkoopprijs)} / stuk
                          {p.is_klantprijs && <span className="line-through ml-1 opacity-70">{fmtEUR(p.standaard_verkoopprijs)}</span>}
                        </div>
                      </div>
                      <Input type="number" min="1" step="1" className="w-20"
                        value={r.aantal} onChange={(e)=>setAantal(r.product_id, parseFloat(e.target.value) || 0)} />
                      <div className="w-24 text-right text-sm font-medium">{fmtEUR((Number(r.aantal)||0)*(Number(p.verkoopprijs)||0))}</div>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={()=>removeProduct(r.product_id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
            <div>
              <Label>Bedrag (€) {f.regels.length > 0 && <span className="text-xs text-muted-foreground">— auto van artikelen</span>}</Label>
              <Input type="number" step="0.01" disabled={f.regels.length > 0}
                value={f.regels.length > 0 ? totaalRegels.toFixed(2) : f.bedrag}
                onChange={(e)=>setF({...f,bedrag:e.target.value})} />
            </div>
            <div><Label>Datum</Label><Input type="date" value={f.datum} onChange={(e)=>setF({...f,datum:e.target.value})} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="vk-submit">Aanmaken & openen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Verkoopkansen() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [totaal, setTotaal] = useState(0);
  const [counts, setCounts] = useState({ openstaand: 0, gearchiveerd: 0, betaling_controleren: 0 });
  const [pipelineBedrag, setPipelineBedrag] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = usePersistedState("kansen:q", "");
  const [qDebounced, setQDebounced] = useState(q);
  const [fase, setFase] = usePersistedState("kansen:fase", "");
  const [datumVan, setDatumVan] = usePersistedState("kansen:datumVan", "");
  const [datumTot, setDatumTot] = usePersistedState("kansen:datumTot", "");
  const [tab, setTab] = usePersistedState("kansen:tab", "openstaand");
  const [selected, setSelected] = useState(() => new Set());
  const perPage = 50;

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const load = async (p = page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/verkoopkansen/pagina", { params: {
        tab, q: qDebounced, fase, datum_van: datumVan, datum_tot: datumTot, page: p, page_size: perPage } });
      setItems(data.items || []); setTotaal(data.totaal || 0);
      setCounts(data.counts || {}); setPipelineBedrag(data.pipeline_bedrag || 0);
      setPage(data.page || p);
    } catch (e) { console.warn("Kansen laden mislukt:", e); setItems([]); }
    finally { setLoading(false); }
  };

  const batchVoltooid = async (val) => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      await api.post("/verkoopkansen/batch/voltooid", { ids, voltooid: val });
      toast.success(`${ids.length} kans(en) ${val ? "voltooid" : "heropend"}`);
      setSelected(new Set()); load(page);
    } catch { toast.error("Bijwerken mislukt"); }
  };

  useEffect(() => { const t = setTimeout(() => setQDebounced(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); /* eslint-disable-next-line */ }, [tab, qDebounced, fase, datumVan, datumTot]);
  useEffect(() => { load(page); /* eslint-disable-next-line */ }, [tab, qDebounced, fase, datumVan, datumTot, page]);

  const openCount = counts.openstaand || 0;
  const archCount = counts.gearchiveerd || 0;
  const controleCount = counts.betaling_controleren || 0;

  const { sortedRows: sorted, SortHeader } = useSortable(items, "datum", "desc");
  const allSelected = sorted.length > 0 && sorted.every(it => selected.has(it.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(sorted.map(it => it.id)));
  const totalPages = Math.max(1, Math.ceil(totaal / perPage));

  const onCreated = (kans) => { load(1); navigate(`/verkoopkansen/${kans.id}`); };
  const openKans = (kid) => {
    slaOverzichtNavOp("verkoopkansen", sorted.map(r => r.id), window.location.pathname + window.location.search);
    navigate(`/verkoopkansen/${kid}`);
  };

  return (
    <div className="space-y-6" data-testid="kans-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-muted-foreground">Verkoop</div>
          <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Verkoopkansen</h1>
          <p className="text-sm text-muted-foreground mt-1">{totaal} kans(en) · pipeline {fmtEUR(pipelineBedrag)}</p>
        </div>
        <NewKansDialog onCreated={onCreated} />
      </div>

      {loading && <LaadBalk label="Verkoopkansen laden" />}

      <div className="flex gap-1 border-b border-border" data-testid="kans-tabs">
        <button onClick={() => setTab("openstaand")} data-testid="tab-openstaand"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab==="openstaand" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          Openstaand ({openCount})
        </button>
        <button onClick={() => setTab("gearchiveerd")} data-testid="tab-gearchiveerd"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab==="gearchiveerd" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          Gearchiveerd ({archCount})
        </button>
        <button onClick={() => setTab("betaling_controleren")} data-testid="tab-betaling-controleren"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab==="betaling_controleren" ? "border-amber-500 text-amber-600" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
          Betaling controleren{controleCount > 0 ? ` (${controleCount})` : ""}
        </button>
      </div>

      <div className="flex gap-3 flex-wrap items-center bg-card border border-border rounded-xl p-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Zoek op naam, organisatie, ordernummer…" className="pl-9" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="kans-search" />
        </div>
        <Select value={fase || "alle"} onValueChange={(v)=>setFase(v==="alle"?"":v)}>
          <SelectTrigger className="w-[200px]" data-testid="kans-fase-filter"><SelectValue placeholder="Fase" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">— alle fases —</SelectItem>
            {FASES.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" className="w-[160px]" value={datumVan} onChange={(e)=>setDatumVan(e.target.value)} data-testid="datum-van" />
        <Input type="date" className="w-[160px]" value={datumTot} onChange={(e)=>setDatumTot(e.target.value)} data-testid="datum-tot" />
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-primary/5 border border-primary/30 rounded-xl px-4 py-2.5" data-testid="batch-toolbar">
          <span className="text-sm font-medium">{selected.size} geselecteerd</span>
          <Button size="sm" onClick={() => batchVoltooid(true)} data-testid="batch-voltooid-btn">
            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Markeer als voltooid
          </Button>
          <Button size="sm" variant="outline" onClick={() => batchVoltooid(false)} data-testid="batch-heropen-btn">Heropenen</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="ml-auto">Wis selectie</Button>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wide">
            <tr>
              <th className="w-10 px-3" onClick={(e)=>e.stopPropagation()}>
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-kansen" aria-label="Selecteer alles" />
              </th>
              <SortHeader k="naam">Naam</SortHeader>
              <SortHeader k="fase">Fase</SortHeader>
              <SortHeader k="organisatie" accessor={(it)=>it.organisatie_naam || ""}>Organisatie</SortHeader>
              <SortHeader k="campagne" accessor={(it)=>it.campagne_naam || ""}>Campagne</SortHeader>
              <SortHeader k="aantal_regels" align="right" accessor={(it)=>Number(it.aantal_regels||0)}>Regels</SortHeader>
              <SortHeader k="bedrag" align="right" accessor={(it)=>Number(it.bedrag||0)}>Bedrag</SortHeader>
              <SortHeader k="datum">Datum</SortHeader>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted.map(it => (
              <tr key={it.id} className={`hover:bg-secondary/40 cursor-pointer transition-colors ${it.voltooid ? "bg-primary/[0.04]" : ""}`}
                  onClick={() => openKans(it.id)}
                  data-testid={`kans-row-${it.id}`}>
                <td className="px-3 py-3" onClick={(e)=>{ e.stopPropagation(); toggleSel(it.id); }}>
                  <Checkbox checked={selected.has(it.id)} onCheckedChange={()=>toggleSel(it.id)} data-testid={`select-kans-${it.id}`} aria-label="Selecteer kans" />
                </td>
                <td className="p-3 font-medium">
                  <span className="inline-flex items-center gap-1.5">
                    {it.voltooid && <CheckCircle2 className="h-4 w-4 text-primary" data-testid={`voltooid-badge-${it.id}`} />}
                    {it.naam || "(zonder naam)"}
                  </span>
                </td>
                <td className="p-3">
                  <Badge variant={it.fase === "Deal!" ? "default" : it.fase === "Afgevallen" ? "destructive" : "outline"}>
                    {it.fase}
                  </Badge>
                </td>
                <td className="p-3" onClick={(e)=>e.stopPropagation()}>
                  <Link to={`/organisaties/${it.relatie_id}`} className="text-primary hover:underline">
                    {it.organisatie_naam || "—"}
                  </Link>
                </td>
                <td className="p-3 text-muted-foreground">{it.campagne_naam || "—"}</td>
                <td className="p-3 text-right text-muted-foreground">{it.aantal_regels || 0}</td>
                <td className="p-3 text-right font-medium">{fmtEUR(it.bedrag)}</td>
                <td className="p-3 text-muted-foreground">{fmtDate(it.datum)}</td>
                <td className="p-3 text-right text-muted-foreground"><ChevronRight className="h-4 w-4 inline" /></td>
              </tr>
            ))}
            {sorted.length === 0 && <TabelLaadRij colSpan={9} loading={loading} />}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm" data-testid="kansen-paginatie">
          <span className="text-muted-foreground">{totaal} kansen · pagina {page} / {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page<=1||loading} onClick={()=>setPage(p=>Math.max(1,p-1))} data-testid="kans-vorige-pagina">Vorige</Button>
            <Button variant="outline" size="sm" disabled={page>=totalPages||loading} onClick={()=>setPage(p=>Math.min(totalPages,p+1))} data-testid="kans-volgende-pagina">Volgende</Button>
          </div>
        </div>
      )}
    </div>
  );
}
